import { ReactNode, useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  Stack,
  TimeXAxis,
  Typography,
  YAxis,
} from '@cast/design-system';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { TopMostExpensiveGroupDatapoint } from '@cast/split-me/types';
import { attachTimeTicks } from '@cast/split-me/utils';

import { ChartWithTopWorkloadsCard } from '../../../../_components/ui/ChartWithTopWorkloadsCard';
import { WidgetLayout } from '../../../../_components/ui/WidgetLayout';

type CrossAzChartDatum = {
  timestamp: string;
  chartTimestampUnix: number;
  values: Record<string, number | null>;
};

type CrossAzTopItem = {
  id: string;
  name: string;
  color: string;
  value: number;
};

type CrossAzChartProps = {
  chartData: CrossAzChartDatum[];
  topItems: { id: string; name: string; color: string }[];
  timeZone: string;
  unit: string;
  formatTick: (value: number) => string;
  formatTooltipValue: (value: string | number) => ReactNode;
  isLoading: boolean;
  testId: string;
};

const CrossAzChart = ({
  chartData,
  topItems,
  timeZone,
  unit,
  formatTick,
  formatTooltipValue,
  isLoading,
  testId,
}: CrossAzChartProps) => (
  <Stack flex={1} minHeight={200}>
    <ChartContainer<CrossAzChartDatum>
      data={chartData}
      isLoading={isLoading}
      sx={{ width: '100%', height: '100%' }}
      margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
      testId={testId}
    >
      <ChartGrid hideVertical />
      {topItems.map((item) => (
        <Line<CrossAzChartDatum>
          key={item.id}
          dataKey={(d) => d.values[item.id] ?? 0}
          stroke={item.color}
          strokeWidth={2}
          dot={false}
          tooltipConfig={{ displayName: item.name }}
        />
      ))}
      <YAxis
        tickCount={5}
        tickFormatter={(value: string | number) =>
          `${formatTick(Number(value))}${unit}`
        }
        padding={{ top: 30 }}
        allowDecimals
      />
      <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
      <ChartTooltip<CrossAzChartDatum>
        testId="data-popper"
        header={(headerProps) => (
          <ChartTooltipTimestampHeader
            data={{ timestamp: headerProps.data.timestamp ?? '' }}
            timezone={timeZone}
            testId={headerProps.testId}
          />
        )}
        formatValue={(value) => formatTooltipValue(value ?? '')}
      />
    </ChartContainer>
  </Stack>
);

export type CrossAzCommunicationCardProps = {
  title: string;
  testId: string;
  topItems: CrossAzTopItem[];
  datapoints: TopMostExpensiveGroupDatapoint;
  timeZone: string;
  unit: string;
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  formatTooltipValue: (value: string | number) => ReactNode;
  isLoading: boolean;
  isFetching: boolean;
  error?: Error | null;
  refetch: () => void;
  renderListEndContent?: (item: CrossAzTopItem) => ReactNode;
};

export const CrossAzCommunicationCard = ({
  title,
  testId,
  topItems,
  datapoints,
  timeZone,
  unit,
  formatValue,
  formatTick,
  formatTooltipValue,
  isLoading,
  isFetching,
  error,
  refetch,
  renderListEndContent,
}: CrossAzCommunicationCardProps) => {
  const hasError = !!error;
  const { toggleMetric, isMetricDisabled } = useDisabledMetricsBoxes<string>();

  const chartData = useMemo<CrossAzChartDatum[]>(
    () => attachTimeTicks(datapoints || []),
    [datapoints]
  );

  const listItems = useMemo(
    () =>
      topItems.map((item) => ({
        id: item.id,
        name: item.name,
        color: item.color,
        value: renderListEndContent
          ? renderListEndContent(item)
          : formatValue(item.value),
      })),
    [topItems, renderListEndContent, formatValue]
  );

  const visibleTopItems = useMemo(
    () => topItems.filter((item) => !isMetricDisabled(item.id)),
    [topItems, isMetricDisabled]
  );

  const chartTestId = `${testId}-chart`;

  const cardContent = (
    <ChartWithTopWorkloadsCard
      testId={testId}
      leftHeader={<Typography variant="A1">{title}</Typography>}
      rightHeader={<Typography variant="A1">Highest to Lowest</Typography>}
      chart={
        <CrossAzChart
          chartData={chartData}
          topItems={visibleTopItems}
          timeZone={timeZone}
          unit={unit}
          formatTick={formatTick}
          formatTooltipValue={formatTooltipValue}
          isLoading={isLoading}
          testId={chartTestId}
        />
      }
      items={listItems}
      isListLoading={isLoading && !topItems.length}
      onItemClick={toggleMetric}
      isItemDisabled={isMetricDisabled}
    />
  );

  if (isLoading || isFetching) {
    return cardContent;
  }

  if (hasError) {
    return (
      <WidgetLayout title={title} testId={testId} padding={false}>
        <FailedToLoad title={`Failed to load ${title}`} refresh={refetch} />
      </WidgetLayout>
    );
  }

  return cardContent;
};
